














































































































import { Component, Inject, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import Context from "@/mixins/context";
import PaymentStatusMixin from "@/mixins/paymentStatusMixin";
import PurchaseEventMixin from "@/mixins/purchaseEventMixin";

// Components
import ExternalAuthMixin from "@/components/app/global/payments/externalAuthMixin.vue";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import { IShopProviderMethods } from "@/models/providers/basket/shop";
import { IState } from "@/store";
import { IInvoice } from "@/models/invoices";
import { QUERY_PARAMS } from "@/data/constants";
import {
  SelectedPaymentMethod,
  PaymentMethodType
} from "@/models/selectPaymentMethod";

@Component({
  components: {
    "order-items": () =>
      import("@/components/app/global/orders/orderItems.vue"),
    "order-details": () =>
      import("@/components/app/global/orders/orderDetails.vue"),
    "order-status-summary": () =>
      import("@/components/app/global/orders/orderStatusSummary.vue"),
    "pay-invoice-cta": () =>
      import("@/components/app/global/invoices/payInvoiceCta.vue"),
    "cancel-order-cta": () =>
      import("@/components/app/global/orders/cancelOrderCta.vue")
  },

  computed: {
    ...mapState({
      user: (state: IState) => state.user,
      isDue(state, getters) {
        return getters[`data/${this.ordersDataModule}/isDue`](this.basket);
      },
      isPaid(state, getters) {
        return getters[`data/${this.ordersDataModule}/isPaid`](this.basket);
      }
    })
  }
})
export default class BasketComplete extends Mixins(
  Context,
  PaymentStatusMixin,
  PurchaseEventMixin,
  ExternalAuthMixin
) {
  // State
  public isDue!: boolean;
  public isPaid!: boolean;
  public selectedPaymentMethod: SelectedPaymentMethod | null = null;

  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly $basketData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  @Inject({ from: "shopProviderMethods" })
  public readonly $shopMethods!: IShopProviderMethods;

  // Variables
  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  public ordersDataModule: DataModules = DataModules.ORDERS;
  public gatewaysDataModule: DataModules = DataModules.BRAND_GATEWAYS;

  public hasOnlineGateways = false;

  // Life-cycle events

  public async created() {
    this.loadData();
    this.$bus.$on("autopay-invoice-success", this.reloadOnBusEvent);
    this.checkExternalAuthQueryParams();
  }

  public beforeDestroy() {
    this.$basketMethods.clearBasket();
    this.$bus.$off("autopay-invoice-success", this.reloadOnBusEvent);
  }

  // Methods

  public async loadData() {
    try {
      this.hasOnlineGateways = await this.getHasOnline();
      await this.$basketMethods.refreshBasketData();
      this.checkPurchaseEvent(this.basket as IInvoice);

      if (!this.basketData.canEnterComplete) {
        this.$shopMethods.goToShop();
      }

      this.requestError = false;
    } catch (error) {
      if (
        this.$store.getters["api/is401"](error) ||
        this.$store.getters["api/is403"](error) ||
        this.$store.getters["api/is404"](error) ||
        this.$store.getters["api/is409"](error)
      ) {
        this.$basketMethods.forceBasketReset();
      }

      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    }

    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public reloadOnBusEvent({ invoiceId }) {
    if (invoiceId === this.basket?.id) this.refreshBasket();
  }

  public getHasOnline() {
    return this.$store.dispatch(`data/${this.gatewaysDataModule}/hasOnline`, {
      brandId: this.basketData.basketBrandId
    });
  }

  public get externalAuthReturnLocation() {
    const isExternalStore =
      this.selectedPaymentMethod?.type === PaymentMethodType.EXTERNAL_STORE;

    return this.buildResolver({
      returnLocation: {
        name: this.isAdmin ? "adminOrderComplete" : "orderCompleteStep",
        params: {
          basketId: this.basket.id
        },
        query: {
          [QUERY_PARAMS.ORDER_ID]: this.basket.id
        }
      },
      paymentMethodType: this.selectedPaymentMethod?.type,
      initPay: isExternalStore ? { invoiceId: this.basket.id } : undefined
    }).location;
  }

  // Computed

  public get basketData() {
    return this.$basketData();
  }

  public get basket() {
    return this.basketData.basket;
  }

  public async refreshBasket() {
    await this.$basketMethods.getBasket(true);
    this.checkPurchaseEvent(this.basket as IInvoice);
  }

  public async clearBasket() {
    await this.$basketMethods.refreshBasketData();
    this.$basketMethods.set("isComplete", false);
    this.$shopMethods.goToShop();
  }
}
